export const phaseLabels: Record<string, string> = {
  group: "Základní skupiny",
  r32: "Šestnáctifinále",
  r16: "Osmifinále",
  qf: "Čtvrtfinále",
  sf: "Semifinále",
  third: "O třetí místo",
  final: "Finále",
};

export const phaseOrder = ["group", "r32", "r16", "qf", "sf", "third", "final"];

export function generateInviteCode(len = 6) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < len; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

export function formatKickoff(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("cs-CZ", {
    day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export function isLocked(kickoff: string, lockMinutesBefore = 0) {
  return new Date(kickoff).getTime() - lockMinutesBefore * 60_000 <= Date.now();
}

/**
 * Convert a UTC ISO instant to a `YYYY-MM-DDTHH:mm` string in the local TZ,
 * suitable for use as the value of an <input type="datetime-local">.
 *
 * Round-trip with `new Date(value).toISOString()` is lossless to the minute:
 *   db (UTC) → toLocalInput → input value → new Date(value).toISOString() → db (UTC)
 */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function isSameDay(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
}

/* -------------------- bulk tips helpers -------------------- */

export interface BulkLeague {
  id: string;
  tournament_id: string | null;
  lock_minutes_before_kickoff: number;
}
export interface BulkMatch {
  id: string;
  tournament_id?: string | null;
  kickoff_at: string;
}
export interface BulkPrediction {
  league_id: string;
  match_id: string;
  predicted_home_score: number;
  predicted_away_score: number;
}
export interface BulkRow {
  league_id: string;
  match_id: string;
  user_id: string;
  predicted_home_score: number;
  predicted_away_score: number;
}
export interface BulkCopyResult {
  rows: BulkRow[];
  skippedLocked: number;
  skippedExisting: number;
  skippedNoMatch: number;
}

/**
 * Build the rows to upsert when copying predictions from a source league to
 * one or more target leagues. Respects each target league's
 * lock_minutes_before_kickoff setting and (unless overwrite) skips matches
 * that already have a prediction in the target league.
 */
export function buildBulkCopyRows(params: {
  sourceLeagueId: string;
  targetLeagueIds: string[];
  leagues: BulkLeague[];
  matches: BulkMatch[];
  predictions: BulkPrediction[];
  userId: string;
  overwrite: boolean;
  now?: number;
}): BulkCopyResult {
  const { sourceLeagueId, targetLeagueIds, leagues, matches, predictions, userId, overwrite } = params;
  const now = params.now ?? Date.now();
  const matchById: Record<string, BulkMatch> = {};
  matches.forEach((m) => { matchById[m.id] = m; });
  const leagueById: Record<string, BulkLeague> = {};
  leagues.forEach((l) => { leagueById[l.id] = l; });

  const rows: BulkRow[] = [];
  let skippedLocked = 0, skippedExisting = 0, skippedNoMatch = 0;

  const sourcePreds = predictions.filter((p) => p.league_id === sourceLeagueId);
  for (const sp of sourcePreds) {
    const match = matchById[sp.match_id];
    if (!match) { skippedNoMatch++; continue; }
    for (const tid of targetLeagueIds) {
      const tLeague = leagueById[tid];
      if (!tLeague) { skippedNoMatch++; continue; }
      const lockedAt = new Date(match.kickoff_at).getTime() - tLeague.lock_minutes_before_kickoff * 60_000;
      if (lockedAt <= now) { skippedLocked++; continue; }
      const existing = predictions.find((p) => p.league_id === tid && p.match_id === sp.match_id);
      if (existing && !overwrite) { skippedExisting++; continue; }
      rows.push({
        league_id: tid,
        match_id: sp.match_id,
        user_id: userId,
        predicted_home_score: sp.predicted_home_score,
        predicted_away_score: sp.predicted_away_score,
      });
    }
  }
  return { rows, skippedLocked, skippedExisting, skippedNoMatch };
}

